import { useState, useEffect, useCallback } from 'react';
import { estimateAPI } from '../../services/api';

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' }
];

/**
 * EstimateAccuracy — dashboard widget showing aggregate accuracy statistics
 * for freight cost estimates.
 *
 * Props:
 *   - days (optional, default 90): initial period in days
 */
const EstimateAccuracy = ({ days: initialDays = 90 }) => {
  const [days, setDays] = useState(initialDays);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await estimateAPI.getAccuracyStats({ days });
      setStats(result.data || null);
    } catch (err) {
      setError('Failed to load accuracy stats');
      console.error('EstimateAccuracy fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="estimate-accuracy">
      {/* Header with period selector */}
      <div className="estimate-accuracy-header">
        <h3>Estimate Accuracy</h3>
        <div className="estimate-accuracy-periods">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`btn btn-sm ${days === opt.value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDays(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="loading">Loading accuracy stats...</p>}
      {error && <div className="error-message">{error}</div>}

      {stats && !loading && (
        <div className="estimate-accuracy-body">
          {/* Summary stats */}
          <div className="stat-grid scorecard-stats">
            <div className="stat-item">
              <span className="stat-value">{stats.totalEstimates ?? 0}</span>
              <span className="stat-label">Total Estimates</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.withActuals ?? 0}</span>
              <span className="stat-label">With Actuals</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">
                {stats.meanDeltaPercent != null ? `${stats.meanDeltaPercent.toFixed(1)}%` : 'N/A'}
              </span>
              <span className="stat-label">Mean Delta</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">
                {stats.medianDeltaPercent != null ? `${stats.medianDeltaPercent.toFixed(1)}%` : 'N/A'}
              </span>
              <span className="stat-label">Median Delta</span>
            </div>
          </div>

          {/* Accuracy bands */}
          {stats.bands && (
            <div className="estimate-accuracy-bands">
              <h4>Accuracy Bands</h4>
              {renderBand('Within 10%', stats.bands.within10, stats.withActuals)}
              {renderBand('Within 20%', stats.bands.within20, stats.withActuals)}
              {renderBand('Within 30%', stats.bands.within30, stats.withActuals)}
            </div>
          )}

          {!stats.bands && stats.withActuals === 0 && (
            <p className="text-muted" style={{ marginTop: '1rem' }}>
              No actuals data available yet for accuracy comparison.
            </p>
          )}
        </div>
      )}

      {!loading && !error && !stats && (
        <p className="text-muted">No accuracy data available.</p>
      )}
    </div>
  );
};

/**
 * Renders a single accuracy band with a progress bar.
 */
function renderBand(label, count, total) {
  if (count == null || total == null || total === 0) return null;
  const percent = Math.round((count / total) * 100);
  return (
    <div className="estimate-accuracy-band">
      <div className="estimate-accuracy-band-header">
        <span className="estimate-accuracy-band-label">{label}</span>
        <span className="estimate-accuracy-band-value">{count}/{total} ({percent}%)</span>
      </div>
      <div className="estimate-accuracy-band-bar">
        <div
          className="estimate-accuracy-band-fill"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export default EstimateAccuracy;
